import milestoneTypes from '../../consts/loyalty/milestoneTypes';
import valueUtils from '../../utils/valueUtils';
import storageService from '../storageService';

type MilestoneType = (typeof milestoneTypes)[keyof typeof milestoneTypes];

interface PromiseData {
    resolve: (value: MilestoneType) => void;
    reject: (error: Error) => void;
    promise: Promise<MilestoneType>;
}

interface ICompletionMessageQueue {
    initQueue(): void;
    add(milestoneType: MilestoneType): void;
    has(milestoneType: MilestoneType): boolean;
    delete(milestoneType: MilestoneType): void;
    waitForCompletion(milestoneType: MilestoneType): Promise<MilestoneType>;
    rejectPending(milestoneType: MilestoneType, error: Error): void;
}

const completionMessageQueue = (() => {
    const queue = new Set<MilestoneType>();
    const pendingPromises = new Map<MilestoneType, PromiseData>();

    return {
        initQueue: (): void => {
            try {
                const storedQueue = storageService.getCompletionMessageQueue();

                if (storedQueue) {
                    storedQueue.forEach((milestoneType: MilestoneType) => {
                        queue.add(milestoneType);
                    });
                }
            } catch (error) {
                console.error('completionMessageQueue: Error initializing queue from localStorage:', error);
            }
        },
        add: (milestoneType: MilestoneType): void => {
            queue.add(milestoneType);

            storageService.addItemToCompletionMessageQueue(milestoneType);

            const promiseData = pendingPromises.get(milestoneType);

            if (promiseData) {
                promiseData.resolve(milestoneType);
                pendingPromises.delete(milestoneType);
            }
        },
        has: (milestoneType: MilestoneType): boolean => {
            return queue.has(milestoneType);
        },
        delete: (milestoneType: MilestoneType): void => {
            queue.delete(milestoneType);

            storageService.deleteItemFromCompletionMessageQueue(milestoneType);
        },
        waitForCompletion: (milestoneType: MilestoneType): Promise<MilestoneType> => {
            if (queue.has(milestoneType)) {
                return Promise.resolve(milestoneType);
            }
            const existingPromiseData = pendingPromises.get(milestoneType);

            if (existingPromiseData && !valueUtils.isEmpty(existingPromiseData.promise)) {
                return existingPromiseData.promise;
            }

            const promiseData = {} as PromiseData;
            const promise = new Promise<MilestoneType>((resolve, reject) => {
                promiseData.resolve = resolve;
                promiseData.reject = reject;
            });

            promiseData.promise = promise;
            pendingPromises.set(milestoneType, promiseData);
            return promise;
        },
        rejectPending: (milestoneType: MilestoneType, error: Error) => {
            const promiseData = pendingPromises.get(milestoneType);

            if (promiseData) {
                promiseData.reject(error);
                pendingPromises.delete(milestoneType);
            }
        },
    } satisfies ICompletionMessageQueue;
})();

export default completionMessageQueue;
